import React, { useEffect, useRef, useState } from "react";
import EditorJS from "@editorjs/editorjs";
import ImageTool from "@editorjs/image";
import Header from '@editorjs/header'; 
import List from '@editorjs/list';
import Quote from '@editorjs/quote';
import Code from '@editorjs/code';
import Table from '@editorjs/table';
import Embed from '@editorjs/embed';
import Delimiter from '@editorjs/delimiter';
import Paragraph from '@editorjs/paragraph';
import Warning from '@editorjs/warning';
import Marker from '@editorjs/marker';
import InlineCode from '@editorjs/inline-code';
import './EditorDarkTheme.css'; // Import dark theme

const EnhancedBlogEditor = ({ onChange, initialData = null }) => {
  const editorRef = useRef(null);
  const isInitialized = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const API_HOST = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const authToken = sessionStorage.getItem("authToken");
    
    // Only initialize once per mount
    if (!editorRef.current && !isInitialized.current) {
      isInitialized.current = true;
      
      // Format initialData to EditorJS format if it's an array
      let formattedData = initialData;
      if (Array.isArray(initialData)) {
        formattedData = { blocks: initialData };
      }
      
      editorRef.current = new EditorJS({
        holder: "editorjs-enhanced",
        data: formattedData || undefined,
        defaultBlock: 'paragraph', // Set default but allow selection
        tools: {
          paragraph: {
            class: Paragraph,
            inlineToolbar: true,
          },
          header: {
            class: Header,
            config: {
              placeholder: 'Enter a header',
              levels: [1, 2, 3, 4, 5, 6],
              defaultLevel: 2
            }
          },
          list: {
            class: List,
            inlineToolbar: true,
            config: {
              defaultStyle: 'unordered'
            }
          },
          quote: {
            class: Quote,
            inlineToolbar: true,
            config: {
              quotePlaceholder: 'Enter a quote',
              captionPlaceholder: 'Quote author',
            },
          },
          warning: {
            class: Warning,
            inlineToolbar: true,
            config: {
              titlePlaceholder: 'Title',
              messagePlaceholder: 'Message',
            },
          },
          marker: {
            class: Marker,
          },
          code: {
            class: Code,
            config: {
              placeholder: 'Enter code'
            }
          },
          inlineCode: {
            class: InlineCode,
          },
          table: {
            class: Table,
            inlineToolbar: true,
            config: {
              rows: 2,
              cols: 3,
            },
          },
          embed: {
            class: Embed,
            config: {
              services: {
                youtube: true,
                twitter: true,
                instagram: true,
                codepen: true,
                facebook: true
              }
            }
          },
          delimiter: Delimiter,
          image: {
            class: ImageTool,
            config: {
              uploader: {
                async uploadByFile(file) {
                  const formData = new FormData();
                  formData.append("image", file);
                  const response = await fetch(`${API_HOST}blogs/upload`, {
                    method: "POST",
                    body: formData,
                    headers: {
                      "auth-token": authToken
                    }
                  });
                  const data = await response.json();
                  return { success: 1, file: { url: data.imageUrl } };
                },
              },
            },
          },
        },
        onChange: async () => {
          const content = await editorRef.current.save();
          onChange(content);
        },
        onReady: () => {
          setIsReady(true);
        }
      });
    }

    return () => {
      if (editorRef.current && typeof editorRef.current.destroy === "function") {
        editorRef.current.destroy();
        editorRef.current = null;
        isInitialized.current = false;
      }
    };
  }, []); // Empty dependency array - only initialize once

  return (
    <div
      id="editorjs-enhanced"
      style={{
        minHeight: "400px",
        overflowY: "auto",
        background: "#1e1e1e",
        color: "#fff",
        padding: "20px",
        paddingLeft: "30px",
        borderRadius: "8px",
        border: "1px solid #333"
      }}
    ></div>
  );
};

export default EnhancedBlogEditor;
